/**
 * The keys a view persists its arrangement and session state under, for the
 * customer currently in scope.
 *
 * Supplies the CustomerType half of the key builders from the app-layer
 * ScopeContext, so every grid preference — column visibility and order, column
 * widths, stat-card visibility and order — every session-lifetime grid setting
 * — sort, search, pagination, filters, card filters, selection — and the map
 * list's saved columns are all keyed the same way from one place.
 *
 * Safe outside customer scope: staff/global views rendered above any scope
 * Layout resolve to the unscoped segment rather than throwing.
 *
 * Contains:
 * - useGridPreferenceKey — a grid's user-preference key (default export)
 * - useGridSessionKey — a grid's sessionStorage key
 * - useScopedPreferenceKey — a user-preference key for any other base key
 *
 * Pairs with app/ScopeContext, hooks/relayLoadTerm (resolveScopeCustomerTypeId)
 * and datasource/gridPreferenceKey.
 */
import { useContext } from 'react';

import ScopeContext from '@sophocles/app/ScopeContext';
import {
  gridPreferenceKey,
  gridSessionKey,
  scopedPreferenceKey,
} from '@sophocles/data-grids/datasource/gridPreferenceKey';
import { resolveScopeCustomerTypeId } from '@sophocles/hooks/relayLoadTerm';

/**
 * The CustomerType id the enclosing scope Layout seeded, or null above any of
 * them. Reads ScopeContext directly (not useScope(), which throws off-route)
 * so a staff/global view falls back to the unscoped segment.
 *
 * @returns {?(string|number)} the scope customer's CustomerType id, or null.
 */
function useScopeCustomerTypeId() {
  return resolveScopeCustomerTypeId(useContext(ScopeContext));
}

/**
 * @param {?string} persistKey - the grid's own persistence scope (e.g.
 *   'siteGrid'), or null when the grid does not persist.
 * @param {string} suffix - what is being saved, including its version where the
 *   shape is versioned (e.g. 'columnWidths', 'columnVisibility.v2').
 * @returns {?string} `grid.<persistKey>.ct<customerTypeId>.<suffix>`, or null
 *   when there is no persistKey.
 */
export default function useGridPreferenceKey(persistKey, suffix) {
  return gridPreferenceKey(persistKey, useScopeCustomerTypeId(), suffix);
}

/**
 * @param {?string} persistKey - the grid's own persistence scope, or null when
 *   the grid does not persist.
 * @param {string} suffix - which piece of session state (e.g. 'sort').
 * @returns {?string} `grid:<persistKey>:ct<customerTypeId>:<suffix>`, or null
 *   when there is no persistKey.
 */
export function useGridSessionKey(persistKey, suffix) {
  return gridSessionKey(persistKey, useScopeCustomerTypeId(), suffix);
}

/**
 * @param {?string} baseKey - what the arrangement belongs to (e.g.
 *   'map.siteGroupList'), or null when it does not persist.
 * @param {string} suffix - what is being saved (e.g. 'columns').
 * @returns {?string} `<baseKey>.ct<customerTypeId>.<suffix>`, or null when
 *   there is no base key.
 */
export function useScopedPreferenceKey(baseKey, suffix) {
  return scopedPreferenceKey(baseKey, useScopeCustomerTypeId(), suffix);
}
